import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getMenuItems } from '../../config/navigationConfig';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import blackLogo from '../../assets/seazex-black.png';
import whiteLogo from '../../assets/Seazex.png';
import './Sidebar.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Sidebar = ({ userRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { theme, sidebarCollapsed, toggleSidebar } = useTheme();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const menuItems = getMenuItems(userRole);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 992) {
        setIsMobileOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setAvatarFailed(false);
  }, [user]);

  const handleNavigate = (path) => {
    const routeMap = {
      dashboard: `/${userRole}/dashboard`,
      'my-ships': `/${userRole}/my-ships`,
      'post-job': `/${userRole}/post-job`,
      'my-jobs-bids': `/${userRole}/my-jobs-quotations`,
      'my-jobs-quotations': `/${userRole}/my-jobs-quotations`,
      payments: `/${userRole}/payments`,
      certificates: `/${userRole}/certificates`,
      tasks: `/${userRole}/tasks`,
      analytics: `/${userRole}/analytics`,
      'available-jobs': `/${userRole}/available-jobs`,
      'my-offers': `/${userRole}/my-offers`,
      'active-work': `/${userRole}/active-work`,
      'work-reports': `/${userRole}/work-reports`,
      'company-profile': `/${userRole}/company-profile`,
      'verify-service-providers': '/admin/verify-service-providers',
      disputes: '/admin/disputes',
      'payment-releases': '/admin/payment-releases',
      credits: '/credits',
      'credit-wallets': '/admin/credit-wallets',
      'credit-purchases': '/admin/credit-purchases',
      withdrawals: '/admin/withdrawals',
      users: '/admin/users',
      reports: '/admin/reports',
      settings: '/admin/settings'
    };
    const route = routeMap[path] || `/${userRole}/dashboard`;
    navigate(route);
  };

  const currentPath = location.pathname.split('/').pop();

  const getRoleLabel = (role) => {
    if (role === 'owner') return 'Ship Owner';
    if (role === 'service-provider') return 'Service Provider';
    if (role === 'admin') return 'Administrator';
    return 'User';
  };

  const getInitials = () => {
    const source = user?.company || user?.name || '';
    if (!source) return 'U';
    const parts = source.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return source.charAt(0).toUpperCase();
  };

  const getAvatarUrl = () => {
    if (user?.avatar && /^https?:\/\//i.test(user.avatar)) {
      return user.avatar;
    }
    const userId = user?._id || user?.id;
    if (!userId) return null;
    return `${API_BASE}/avatar/${userId}`;
  };

  const avatarUrl = getAvatarUrl();

  const handleProfileClick = () => {
    navigate(`/${userRole}/profile`);
  };

  const sidebarClasses = [
    'sidebar',
    sidebarCollapsed ? 'collapsed' : '',
    isMobileOpen ? 'open' : ''
  ].filter(Boolean).join(' ');

  const logoSrc = theme === 'light' ? blackLogo : whiteLogo;

  return (
    <>
      {isMobileOpen && (
        <div className="sidebar-mobile-overlay" onClick={() => setIsMobileOpen(false)} />
      )}

      <aside className={sidebarClasses}>
        <div className="sidebar-logo-section">
          <div className="logo-wrapper">
            <div className="logo-icon">
              <img src={logoSrc} alt="Seazex" />
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item, index) => (
            <button
              key={index}
              className={`nav-item ${currentPath === item.path ? 'active' : ''}`}
              onClick={() => handleNavigate(item.path)}
              title={sidebarCollapsed ? item.label : ''}
            >
              <div className="nav-icon">
                <i className={`fa-solid ${item.icon.replace('fas ', '')}`}></i>
              </div>
              {!sidebarCollapsed && <span className="nav-label">{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button
            className="sidebar-link-btn"
            onClick={() => navigate('/help-center')}
            title={sidebarCollapsed ? 'Help & Support' : ''}
          >
            <i className="fa-regular fa-circle-question"></i>
            {!sidebarCollapsed && <span>Help &amp; Support</span>}
          </button>

          <button
            className="sidebar-link-btn"
            onClick={() => navigate(`/${userRole}/settings`)}
            title={sidebarCollapsed ? 'Settings' : ''}
          >
            <i className="fa-solid fa-gear"></i>
            {!sidebarCollapsed && <span>Settings</span>}
          </button>

          <button
            className="sidebar-link-btn sidebar-collapse-link"
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <i className={`fa-solid fa-chevron-${sidebarCollapsed ? 'right' : 'left'}`}></i>
            {!sidebarCollapsed && <span>Collapse</span>}
          </button>

          <div
            className="sidebar-user-card"
            onClick={handleProfileClick}
            role="button"
            tabIndex={0}
          >
            <div className="sidebar-user-avatar">
              {avatarUrl && !avatarFailed ? (
                <img
                  src={avatarUrl}
                  alt={user?.name || 'User'}
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                <span>{getInitials()}</span>
              )}
            </div>
            {!sidebarCollapsed && (
              <div className="sidebar-user-info">
                <span className="sidebar-user-name">
                  {user?.name || 'User'}
                  <i className="fa-solid fa-circle-check sidebar-user-verified"></i>
                </span>
                <span className="sidebar-user-role">{getRoleLabel(userRole)}</span>
              </div>
            )}
            {!sidebarCollapsed && (
              <i className="fa-solid fa-chevron-right sidebar-user-arrow"></i>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;